import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mic, X, CheckCircle2, Sparkles, ClipboardList, Snowflake, Thermometer, Sun, Flame } from "lucide-react";

function ProspectSheet({ lead }) {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  useEffect(() => {
    setLoading(true);
    api.post("/ai/prospect-sheet", { lead })
      .then((r) => setSheet(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (loading) return <div className="grid place-items-center py-12"><div className="w-7 h-7 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;
  if (!sheet) return <div className="text-sm text-zinc-500 text-center py-8">L'IA n'a pas pu préparer la fiche.</div>;

  return (
    <div className="space-y-3">
      <div className="bg-emerald-500/10 rounded-2xl p-4 border border-emerald-500/30">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-300"><ClipboardList className="w-4 h-4" />Fiche préparée par Prosperr</div>
        {sheet.identite && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-zinc-500">Nom :</span> <strong>{sheet.identite.nom || "à compléter"}</strong></div>
            <div><span className="text-zinc-500">Prénom :</span> <strong>{sheet.identite.prenom || "à compléter"}</strong></div>
            <div><span className="text-zinc-500">Fonction :</span> <strong>{sheet.identite.fonction || "à compléter"}</strong></div>
            <div><span className="text-zinc-500">Entreprise :</span> <strong>{sheet.identite.entreprise || "à compléter"}</strong></div>
          </div>
        )}
      </div>

      {sheet.contexte_connu?.length > 0 && (
        <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Ce qu'on sait déjà</div>
          <ul className="mt-2 space-y-1 text-sm list-disc ml-4">
            {sheet.contexte_connu.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {sheet.questions_a_poser?.length > 0 && (
        <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Questions à poser</div>
          <ol className="mt-2 space-y-1 text-sm list-decimal ml-4">
            {sheet.questions_a_poser.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}

      {sheet.champs_a_completer?.length > 0 && (
        <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">À compléter pendant la visite</div>
          {sheet.champs_a_completer.map((f, i) => (
            <div key={i}>
              <div className="text-xs font-semibold text-zinc-300 mb-1">{f.label}</div>
              <input
                value={values[f.label] || ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.label]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full rounded-xl border border-[var(--border)] p-2.5 text-sm"
                data-testid={`sheet-field-${i}`}
              />
            </div>
          ))}
        </div>
      )}

      {sheet.signaux_a_detecter?.length > 0 && (
        <div className="bg-amber-500/10 rounded-2xl p-4 border border-amber-100">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-300">Signaux à détecter</div>
          <ul className="mt-2 space-y-1 text-sm list-disc ml-4">
            {sheet.signaux_a_detecter.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {sheet.prochaine_etape_recommandee && (
        <div className="bg-emerald-500/10 rounded-2xl p-4 border border-emerald-100">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-300">Prochaine étape recommandée</div>
          <div className="text-sm text-zinc-200 mt-1">{sheet.prochaine_etape_recommandee}</div>
        </div>
      )}
    </div>
  );
}

const QUESTIONS = [
  "Demandez son principal défi commercial en ce moment",
  "Depuis combien de temps il cherche une solution",
  "Quel est son budget prévu",
  "Qui prend la décision finale",
];

export default function VisitMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [profile, setProfile] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [asked, setAsked] = useState({});
  const [capture, setCapture] = useState({ first: "", last: "", title: "", email: "", phone: "", linkedin: "", notes: "", budget: "", interest: "" });
  const [checkedObjections, setCheckedObjections] = useState([]);
  const [actions, setActions] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/prospects/${id}`);
      setP(data.prospect);
      setCapture((c) => ({ ...c, first: (data.prospect.name || "").split(" ")[0], last: (data.prospect.name || "").split(" ").slice(1).join(" "), title: data.prospect.title || "", email: data.prospect.email || "", phone: data.prospect.phone || "" }));
      const pf = await api.get("/profile");
      setProfile(pf.data);
    })();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [id]);

  const finish = async () => {
    const duration = Math.floor(elapsed / 60);
    setGenerating(true);
    try {
      await api.post("/interactions", {
        prospect_id: id, type: "visit", duration, outcome: capture.interest, notes: capture.notes,
      });
      await api.put(`/prospects/${id}`, {
        status: "Contacté",
        interest_level: capture.interest,
        budget_mentioned: capture.budget,
        notes: capture.notes,
      });
      const { data } = await api.post("/ai/post-visit-actions", {
        lead: p, notes: capture.notes, interest_level: capture.interest, duration,
      });
      setActions(data);
    } catch { toast.error("Erreur IA"); }
    finally { setGenerating(false); }
  };

  const startRec = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) { toast.error("Dictée non supportée"); return; }
    const r = new Rec();
    r.lang = "fr-FR"; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let t = "";
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript + " ";
      setTranscript(t);
    };
    r.onend = () => setRecording(false);
    r.start(); recRef.current = r; setRecording(true);
  };
  const stopRec = () => { recRef.current?.stop(); };

  const processDebrief = async () => {
    if (!transcript) return;
    try {
      const { data } = await api.post("/ai/voice-debrief", { transcript, lead: p });
      await api.put(`/prospects/${id}`, { notes: `${capture.notes}\n\nDébrief vocal: ${data.resume || transcript}`, voice_debrief: transcript });
      toast.success("Débrief structuré ajouté");
      setTranscript("");
    } catch { toast.error("Erreur"); }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  if (!p) return <div className="min-h-screen grid place-items-center bg-[var(--surface)]"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;

  if (actions) {
    return (
      <div className="min-h-screen bg-[var(--surface-alt)] pb-16">
        <div className="bg-emerald-500/100 text-white p-5">
          <div className="text-sm opacity-90">✅ Visite terminée — {mm}:{ss}</div>
          <div className="font-display text-xl font-bold mt-0.5">{p.name} · {p.company}</div>
          <div className="mt-2 inline-block bg-white/20 rounded-full px-3 py-1 text-xs font-bold">Intérêt : {capture.interest || "—"}</div>
        </div>
        <div className="max-w-2xl mx-auto p-5 space-y-4">
          {actions.email && (
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm" data-testid="post-action-email">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">⚡ Maintenant (2h)</div>
              <div className="font-display font-semibold mt-1">📧 Email de suivi</div>
              <div className="mt-2 text-sm"><strong>Objet :</strong> {actions.email.objet}</div>
              <div className="mt-2 text-sm whitespace-pre-wrap bg-[var(--surface-alt)] rounded-lg p-3">{actions.email.corps}</div>
              <div className="flex gap-2 mt-3">
                <Button className="rounded-full bg-emerald-500/100 hover:bg-emerald-500 flex-1" onClick={() => { navigator.clipboard.writeText(`Objet: ${actions.email.objet}\n\n${actions.email.corps}`); toast.success("Copié"); }}>Copier</Button>
                <Button variant="outline" className="rounded-full flex-1" onClick={async () => { await api.post("/actions", { prospect_id: id, type: "email", scheduled_date: new Date().toISOString(), content: JSON.stringify(actions.email) }); toast.success("Programmé"); }}>Programmer</Button>
              </div>
            </div>
          )}
          {actions.call && (
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">📅 Dans 48h</div>
              <div className="font-display font-semibold mt-1">📞 Rappeler</div>
              <div className="text-sm text-zinc-400 mt-1">Meilleur moment : <strong>{actions.call.when}</strong></div>
              <div className="text-xs bg-[var(--surface-alt)] rounded-lg p-2 mt-2">{actions.call.script}</div>
              <Button className="mt-2 rounded-full w-full" variant="outline" onClick={async () => { await api.post("/actions", { prospect_id: id, type: "call", scheduled_date: new Date(Date.now() + 2 * 86400000).toISOString(), content: actions.call.script }); toast.success("Rappel programmé"); }}>Programmer le rappel</Button>
            </div>
          )}
          {actions.week && (
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">📅 Cette semaine</div>
              <div className="font-display font-semibold mt-1">{actions.week.type}</div>
              <div className="text-sm bg-[var(--surface-alt)] rounded-lg p-2 mt-2">{actions.week.message}</div>
              <Button className="mt-2 rounded-full w-full" variant="outline" onClick={() => { navigator.clipboard.writeText(actions.week.message); toast.success("Copié"); }}>Copier le message</Button>
            </div>
          )}
          {actions.recommendation && (
            <div className="bg-amber-500/10 rounded-2xl p-4 border border-amber-100">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-300">💡 Recommandation Prosperr</div>
              <div className="text-sm text-zinc-300 mt-1">{actions.recommendation}</div>
            </div>
          )}

          {/* Voice debrief */}
          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="font-display font-semibold mb-2">🎤 Débrief vocal</div>
            <Textarea rows={3} value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder="Dictez ou tapez vos impressions..." className="rounded-xl" />
            <div className="flex gap-2 mt-2">
              <Button onClick={recording ? stopRec : startRec} className={`rounded-full flex-1 ${recording ? "bg-rose-500/100/100 hover:bg-red-600" : "bg-emerald-500/100 hover:bg-emerald-500"}`} data-testid="record-debrief"><Mic className="w-4 h-4 mr-1" />{recording ? "Stop" : "Dicter"}</Button>
              <Button onClick={processDebrief} variant="outline" className="rounded-full flex-1" data-testid="process-debrief"><Sparkles className="w-4 h-4 mr-1" />Structurer</Button>
            </div>
          </div>

          <Button onClick={() => navigate("/pipeline")} className="w-full rounded-full bg-slate-900 hover:bg-slate-800 h-11 font-semibold" data-testid="finish-visit-flow"><CheckCircle2 className="w-4 h-4 mr-2" />Terminer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-alt)] flex flex-col">
      <div className="bg-emerald-500/100 text-white p-4 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate(-1)} aria-label="close" className="p-2"><X className="w-5 h-5" /></button>
        <div>
          <div className="font-display text-lg font-bold" data-testid="visit-timer">{mm}:{ss}</div>
          <div className="text-xs opacity-90 text-center">{p.name}</div>
        </div>
        <Button onClick={finish} disabled={generating} data-testid="finish-visit" className="rounded-full bg-[var(--surface)] text-emerald-400 hover:bg-[var(--surface)] font-semibold h-9 px-4">
          {generating ? "..." : "Terminer"}
        </Button>
      </div>

      <div className="max-w-2xl mx-auto w-full p-4 flex-1">
        <Tabs defaultValue="guide" className="w-full">
          <TabsList className="grid grid-cols-4 rounded-full bg-slate-200 p-1">
            <TabsTrigger value="sheet" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-sheet">Fiche</TabsTrigger>
            <TabsTrigger value="guide" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-guide">Guide</TabsTrigger>
            <TabsTrigger value="capture" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-capture">Capture</TabsTrigger>
            <TabsTrigger value="context" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-context">Contexte</TabsTrigger>
          </TabsList>

          <TabsContent value="sheet" className="mt-4">
            <ProspectSheet prospect={p} />
          </TabsContent>

          <TabsContent value="guide" className="mt-4 space-y-2">
            {QUESTIONS.map((q, i) => (
              <div key={i} className={`bg-[var(--surface)] rounded-xl p-4 border shadow-sm flex items-start gap-3 ${asked[i] ? "border-emerald-500/40 bg-emerald-500/10/30" : "border-[var(--border-soft)]"}`}>
                <div className="flex-1 text-base font-medium text-[var(--text)]">{q}</div>
                <Button size="sm" variant={asked[i] ? "default" : "outline"} onClick={() => setAsked((a) => ({ ...a, [i]: !a[i] }))} data-testid={`ask-${i}`} className="rounded-full shrink-0">{asked[i] ? "✓" : "Poser"}</Button>
              </div>
            ))}

            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Objections</div>
              <div className="space-y-2">
                {(profile?.objections || []).map((o, i) => (
                  <details key={i} className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] shadow-sm">
                    <summary className="p-3 font-semibold text-sm cursor-pointer list-none flex items-center justify-between">
                      {o.objection || `Objection ${i + 1}`}
                      <span className="text-xs text-emerald-400">Voir</span>
                    </summary>
                    <div className="p-3 pt-0 text-sm text-zinc-300">{o.reponse}</div>
                  </details>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="capture" className="mt-4 space-y-3">
            <Input placeholder="Prénom" value={capture.first} onChange={(e) => setCapture({ ...capture, first: e.target.value })} className="rounded-xl h-12 text-base" data-testid="cap-first" />
            <Input placeholder="Nom" value={capture.last} onChange={(e) => setCapture({ ...capture, last: e.target.value })} className="rounded-xl h-12 text-base" data-testid="cap-last" />
            <Input placeholder="Fonction" value={capture.title} onChange={(e) => setCapture({ ...capture, title: e.target.value })} className="rounded-xl h-12 text-base" />
            <Input placeholder="Email" type="email" value={capture.email} onChange={(e) => setCapture({ ...capture, email: e.target.value })} className="rounded-xl h-12 text-base" />
            <Input placeholder="Téléphone" inputMode="tel" value={capture.phone} onChange={(e) => setCapture({ ...capture, phone: e.target.value })} className="rounded-xl h-12 text-base" />
            <Textarea placeholder="Besoins exprimés..." rows={3} value={capture.notes} onChange={(e) => setCapture({ ...capture, notes: e.target.value })} className="rounded-xl text-base" data-testid="cap-notes" />
            <Input placeholder="Budget mentionné (€)" value={capture.budget} onChange={(e) => setCapture({ ...capture, budget: e.target.value })} className="rounded-xl h-12" />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Niveau d'intérêt</div>
              <div className="grid grid-cols-4 gap-0 border border-[var(--border)]" data-testid="interest-picker">
                {[["Froid", Snowflake], ["Tiède", Thermometer], ["Chaud", Sun], ["Très chaud", Flame]].map(([v, Ic], i) => {
                  const active = capture.interest === v;
                  return (
                    <button
                      key={v}
                      onClick={() => setCapture({ ...capture, interest: v })}
                      data-testid={`interest-${v}`}
                      className={`p-3 flex flex-col items-center justify-center gap-1.5 transition-colors ${
                        i > 0 ? "border-l border-[var(--border)]" : ""
                      } ${
                        active
                          ? "bg-black text-white"
                          : "bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-alt)]/30"
                      }`}
                    >
                      <Ic className="w-5 h-5" strokeWidth={2} />
                      <div className="text-[11px] font-bold leading-none">{v}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="context" className="mt-4 space-y-3 text-sm">
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Votre pitch</div>
              <div className="mt-1 text-zinc-300">{profile?.offer_description || "-"}</div>
            </div>
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Arguments</div>
              <ul className="mt-1 space-y-1">
                {(profile?.arguments || []).map((a, i) => <li key={i} className="text-zinc-300">• {a}</li>)}
              </ul>
            </div>
            <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Prix</div>
              <div className="text-zinc-300">Panier moyen : <strong>{profile?.average_basket?.toLocaleString("fr-FR")} €</strong></div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
